/**
 * RemoteConfigDataService
 *
 * The single place in the codebase that touches `firebase/remote-config`. It
 * owns the RC instance, the Flutter-matching settings, the `defaultConfig`
 * assignment, `fetchAndActivate` / `ensureInitialized` / `activate`, the
 * real-time `onConfigUpdate` subscription, and the raw value reads.
 *
 * Mirrors Flutter's `RemoteConfigService.init()`
 * (lib/services/remote_config_service.dart:110-194) and `close()`
 * (lib/services/remote_config_service.dart:327-329).
 *
 * Per the data-layer surface rule (data-layer.md) no Firebase type
 * (`RemoteConfig`, `Value`, `ConfigUpdate`, `Unsubscribe`) appears in an
 * exported signature — only primitives, plain `string[]`, and a plain
 * unsubscribe closure cross the boundary.
 *
 * Four mechanical web-vs-Flutter SDK differences shape this port:
 * 1. The web SDK has **no `getInt`** — only `getBoolean` / `getString` /
 *    `getNumber`. Flutter's `getInt` (remote_config_service.dart:56-81) is
 *    reproduced by truncating `getNumber` in the domain facade, not here.
 * 2. `defaultConfig` accepts only `{ [key: string]: string | number | boolean }`,
 *    so the `boosts` catalog travels as a JSON **string** and is parsed in the
 *    domain facade (Flutter does the same at remote_config_service.dart:84-108).
 * 3. Web Remote Config needs IndexedDB, so `initialize` is gated on the SDK's
 *    `isSupported()`. When it is unavailable (e.g. some private-mode browsers)
 *    this service stays uninitialized, every getter returns `null`, and the
 *    domain facade degrades to its compile-time defaults instead of throwing.
 * 4. The web SDK's own settings defaults (60 000 ms fetch timeout / 43 200 000 ms
 *    minimum fetch interval) differ from Flutter's, so they are set explicitly.
 *
 * Layering: the defaults map, the key enum, the int truncation and the boosts
 * JSON parse all live in the **domain** facade
 * (`domain/services/remoteConfig/remoteConfigService.ts`). This service takes an
 * opaque `Record<string, string | number | boolean | undefined>` (the caller's
 * map is partial, so `initialize` filters the `undefined` entries out before
 * handing Firebase its `defaultConfig`) and never imports a domain enum,
 * constant or remapper. `appLogger` is the one sanctioned data → domain import
 * (conventions.md → "🪵 Logging"; `console.error` is forbidden).
 */

import {
  activate,
  ensureInitialized,
  fetchAndActivate,
  getBoolean as getRemoteBoolean,
  getNumber as getRemoteNumber,
  getRemoteConfig,
  getString as getRemoteString,
  isSupported,
  onConfigUpdate,
  type RemoteConfig,
} from 'firebase/remote-config';
import app from '@/data/services/firebase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { subscribeToBeforeUnload, subscribeToPageHide } from '@/infrastructure/beforeUnload';

/**
 * Maximum time to wait for a fetch response.
 * Flutter: `fetchTimeout: const Duration(seconds: 10)`
 * (lib/services/remote_config_service.dart:115). The web SDK's own default is
 * 60 000 ms, so this must be set explicitly or the two clients diverge.
 */
const FETCH_TIMEOUT_MILLIS = 10_000;

/**
 * Maximum age of a cached config entry before it is considered stale.
 * Flutter: `minimumFetchInterval: const Duration(days: 1)`
 * (lib/services/remote_config_service.dart:116). The web SDK's own default is
 * 43 200 000 ms (12 hours), so this must be set explicitly.
 */
const MINIMUM_FETCH_INTERVAL_MILLIS = 86_400_000;

export class RemoteConfigDataService {
  private static instance: RemoteConfigDataService;

  /** Firebase type used internally only — never exposed in a public signature. */
  private remoteConfig: RemoteConfig | null = null;

  private constructor() { }

  public static getInstance(): RemoteConfigDataService {
    if (!RemoteConfigDataService.instance) {
      RemoteConfigDataService.instance = new RemoteConfigDataService();
    }
    return RemoteConfigDataService.instance;
  }

  /**
   * Creates the RC instance, applies the Flutter-matching settings and the
   * compile-time defaults, then fetches + activates the server template.
   *
   * Mirrors remote_config_service.dart:110-185. Like Flutter, the fetch is
   * wrapped in a try/catch that only logs and never rethrows
   * (remote_config_service.dart:180-185) — a failed fetch leaves the defaults in
   * place rather than breaking app startup.
   *
   * @param defaults opaque key → value map. Values may be `undefined` because
   *   the caller's map is partial (the server-only keys have no compile-time
   *   default); those entries are filtered out before Firebase sees them.
   * @returns `false` when Remote Config is unsupported in this browser (the
   *   caller should fall back to its compile-time defaults), `true` otherwise.
   */
  async initialize(defaults: Record<string, string | number | boolean | undefined>): Promise<boolean> {
    if (!(await isSupported())) {
      appLogger.warning(
        'Remote Config is not supported in this browser; falling back to compile-time defaults',
        'RemoteConfigDataService.initialize',
      );
      return false;
    }

    this.remoteConfig = getRemoteConfig(app);

    // Flutter: setConfigSettings(fetchTimeout: 10s, minimumFetchInterval: 1 day)
    // (remote_config_service.dart:113-118).
    this.remoteConfig.settings = {
      fetchTimeoutMillis: FETCH_TIMEOUT_MILLIS,
      minimumFetchIntervalMillis: MINIMUM_FETCH_INTERVAL_MILLIS,
    };

    // Flutter: `await _remoteConfig.setDefaults(defaults)`
    // (remote_config_service.dart:176). Firebase's `defaultConfig` property
    // admits only string | number | boolean, so drop the undefined entries.
    const defaultConfig: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(defaults)) {
      if (value !== undefined) {
        defaultConfig[key] = value;
      }
    }
    this.remoteConfig.defaultConfig = defaultConfig;

    try {
      await fetchAndActivate(this.remoteConfig);
      await ensureInitialized(this.remoteConfig);
    } catch (error) {
      appLogger.error(
        'Remote Config could not fetch',
        'RemoteConfigDataService.initialize',
        { error: error as Error },
      );
    }

    return true;
  }

  /**
   * Subscribes to real-time config updates.
   *
   * Mirrors remote_config_service.dart:191-194: every push activates the new
   * template **before** the caller is notified, so the caller's getters already
   * read the fresh values. Do not reorder. When that activation fails the push
   * is logged and skipped — the callback is never invoked against the previous
   * (stale) template.
   *
   * The `Set<string>` of updated keys is converted to a `string[]` here so the
   * Firebase-shaped `ConfigUpdate` stays out of the exported signature.
   *
   * **Web-only teardown guard (no Flutter counterpart).** A Dart app has no
   * document unload, so Flutter's `onError` handler
   * (remote_config_service.dart:191-194) never fires spuriously. On the web,
   * every refresh / navigation aborts the SDK's in-flight
   * `streamFetchInvalidations` request while the outgoing document's JS is still
   * alive, and the SDK reports that abort through the `error` channel below.
   * That is an ordinary lifecycle event, not a failure — and `appLogger.error`
   * ships to the analytics DB, so logging it there would emit a spurious
   * error-level event for every session end of every user. Teardown errors are
   * therefore demoted to `appLogger.debug`; genuine stream failures still reach
   * `appLogger.error` untouched.
   *
   * @returns an unsubscribe closure (Flutter's `close()`,
   *   remote_config_service.dart:327-329). A no-op when RC is uninitialized.
   */
  subscribeToConfigUpdates(onUpdatedKeys: (keys: string[]) => void | Promise<void>): () => void {
    const remoteConfig = this.remoteConfig;
    if (!remoteConfig) {
      return () => { };
    }

    // Latched by `pagehide` (fires on every unload path, Safari/iOS included)
    // and `beforeunload` (earlier, but unreliable on Safari/iOS). Both run
    // BEFORE the document flips to `hidden` and long before the aborted stream
    // reports back, so the flag is always set by the time the abort lands.
    let pageIsGoingAway = false;
    const markPageGoingAway = () => {
      pageIsGoingAway = true;
    };
    const unsubscribeFromPageHide = subscribeToPageHide(markPageGoingAway);
    const unsubscribeFromBeforeUnload = subscribeToBeforeUnload(markPageGoingAway);

    // Set by the returned closure — a stream error that arrives after the caller
    // has already torn the subscription down is likewise teardown, not failure.
    let unsubscribed = false;

    // Deliberately a CONJUNCTION, so this stays a teardown guard and never
    // becomes a blanket silence of the error channel: `pagehide` also fires when
    // the document merely enters the back/forward cache, and such a document can
    // be restored and keep streaming. Once it is visible again the second
    // conjunct is false and genuine failures are error-logged as before. (The
    // visibility check alone would not do: it is also true for a plain
    // backgrounded tab, whose stream errors are real.)
    const isTearingDown = () =>
      unsubscribed
      || (pageIsGoingAway
        && (typeof document === 'undefined' || document.visibilityState === 'hidden'));

    const unsubscribeFromSdk = onConfigUpdate(remoteConfig, {
      next: async (update) => {
        // `ConfigUpdateObserver.next` returns `void` — the SDK never awaits this
        // promise and the `error` channel below only carries STREAM errors, so a
        // rejection here would escape as an unhandled rejection. Contain it: a
        // failed activate() leaves the previous template active, and dispatching
        // the new key list against stale values would be worse than skipping.
        try {
          await activate(remoteConfig);
        } catch (error) {
          appLogger.error(
            'Remote Config could not activate a real-time update; push skipped',
            'RemoteConfigDataService.subscribeToConfigUpdates',
            { error: error as Error },
          );
          return;
        }

        await onUpdatedKeys(Array.from(update.getUpdatedKeys()));
      },
      error: (error) => {
        if (isTearingDown()) {
          // Console-only (analyticsService routes debug/info away from
          // Firestore) — observability without polluting error analytics.
          appLogger.debug(
            'Remote Config real-time update stream closed by page teardown',
            'RemoteConfigDataService.subscribeToConfigUpdates',
            { context: { errorMessage: error.message } },
          );
          return;
        }

        appLogger.error(
          'Remote Config real-time update stream error',
          'RemoteConfigDataService.subscribeToConfigUpdates',
          { error },
        );
      },
      complete: () => { },
    });

    return () => {
      unsubscribed = true;
      unsubscribeFromPageHide();
      unsubscribeFromBeforeUnload();
      unsubscribeFromSdk();
    };
  }

  /**
   * Flutter: `_remoteConfig.getBool(key)` (remote_config_service.dart:20-27).
   * `null` when RC is unsupported or `initialize` has not run, so the domain
   * facade can fall back to its compile-time default.
   */
  getBoolean(key: string): boolean | null {
    if (!this.remoteConfig) {
      return null;
    }
    return getRemoteBoolean(this.remoteConfig, key);
  }

  /**
   * Flutter: `_remoteConfig.getString(key)` (remote_config_service.dart:28-29).
   * `null` when RC is unsupported or `initialize` has not run.
   */
  getString(key: string): string | null {
    if (!this.remoteConfig) {
      return null;
    }
    return getRemoteString(this.remoteConfig, key);
  }

  /**
   * Flutter: `_remoteConfig.getDouble(key)` (remote_config_service.dart:30-55).
   * There is no `getInt` on web — the int truncation Flutter does at
   * remote_config_service.dart:56-81 lives in the domain facade.
   * `null` when RC is unsupported or `initialize` has not run.
   */
  getNumber(key: string): number | null {
    if (!this.remoteConfig) {
      return null;
    }
    return getRemoteNumber(this.remoteConfig, key);
  }

  /**
   * Clears the RC instance reference (Flutter's `close()`,
   * remote_config_service.dart:327-329). Tearing down the real-time
   * subscription is the caller's job via the closure returned by
   * `subscribeToConfigUpdates`.
   */
  dispose(): void {
    this.remoteConfig = null;
  }
}

export const remoteConfigDataService = RemoteConfigDataService.getInstance();
